import React, { createContext, useContext, useReducer, useRef, useEffect, useCallback, useMemo } from "react";
import logger from '../utils/logger';

const TranscriptionContext = createContext();
const STORAGE_KEY = "transcription_state";

// Move initialState to a ref so it persists across re-renders
const getInitialState = () => {
  try {
    const persisted = localStorage.getItem(STORAGE_KEY);
    if (persisted) {
      const parsed = JSON.parse(persisted);
      logger.info('📝 [TranscriptionContext] Restored state from storage:', parsed);
      return parsed;
    }
  } catch (error) {
    logger.error('📝 [TranscriptionContext] Failed to restore state:', error);
  }
  
  return {
    transcript: [],
    isRecording: false,
    isPaused: false,
    currentIndex: 0
  };
};

function transcriptionReducer(state, action) {
  console.log('📝 [TranscriptionContext] Reducer called with:', {
    actionType: action.type,
    currentState: {
      transcriptLength: state.transcript?.length,
      currentIndex: state.currentIndex,
      isRecording: state.isRecording,
      isPaused: state.isPaused
    },
    payload: action.payload
  });
  
  const newState = (() => {
    switch (action.type) {
      case "START_RECORDING":
        return { ...state, isRecording: true, isPaused: false };
      case "STOP_RECORDING":
        return { ...state, isRecording: false, isPaused: false };
      case "PAUSE_RECORDING":
        return { ...state, isPaused: true };
      case "RESUME_RECORDING":
        return { ...state, isPaused: false };
      case "ADD_TRANSCRIPT_LINE": {
        // Safety check for duplicate entries
        const isDuplicate = state.transcript?.some(line => 
          line.time === action.payload.time && 
          line.text === action.payload.text
        );

        if (isDuplicate) {
          console.warn('⚠️ Duplicate transcript line detected:', action.payload);
          return state;
        }

        const newTranscript = Array.isArray(state.transcript) 
          ? [...state.transcript, action.payload]
          : [action.payload];

        console.log('📝 Adding line to transcript:', {
          newLength: newTranscript.length,
          addedLine: action.payload
        });

        return {
          ...state,
          transcript: newTranscript,
        };
      }
      case "SET_TRANSCRIPT": {
        const newTranscript = Array.isArray(action.payload) ? action.payload : [];
        console.log('📝 Setting full transcript:', {
          newLength: newTranscript.length,
          oldLength: state.transcript?.length
        });
        return {
          ...state,
          transcript: newTranscript,
        };
      }
      case "SET_CURRENT_INDEX": {
        const nextIndex = action.payload;
        // Allow currentIndex to advance beyond transcript length
        // This is important for demo playback where transcript entries
        // may be added after the current index advances
        console.log('📝 Setting current index:', {
          oldIndex: state.currentIndex,
          requestedIndex: nextIndex,
          transcriptLength: state.transcript?.length,
          allowAdvance: true
        });
        
        return {
          ...state,
          currentIndex: Math.max(0, nextIndex),
        };
      }
      case "RESET_TRANSCRIPT":
        return getInitialState();
      case "CLEANUP":
        if (state.isRecording) {
          return { ...state, isRecording: false, isPaused: true };
        }
        return state;
      default:
        return state;
    }
  })();

  // Log state changes
  if (newState !== state) {
    console.log('📝 [TranscriptionContext] State updated:', {
      transcriptLength: newState.transcript?.length,
      currentIndex: newState.currentIndex,
      isRecording: newState.isRecording,
      isPaused: newState.isPaused
    });
  }

  // Persist state changes to localStorage
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
  } catch (error) {
    logger.error('📝 [TranscriptionContext] Failed to persist state:', error);
  }

  return newState;
}

export function TranscriptionProvider({ children }) {
  // Performance monitoring ref
  const performanceRef = useRef({
    mountTime: 0,
    updateCount: 0
  });

  // Create a stable reference to the state
  const stateRef = useRef(getInitialState());
  
  // Initialize reducer with the ref's current value
  const [state, dispatch] = useReducer(transcriptionReducer, stateRef.current);

  // Track mount performance
  useEffect(() => {
    console.time("TranscriptionProvider mount");
    performanceRef.current.mountTime = performance.now();
    
    return () => {
      const mountDuration = performance.now() - performanceRef.current.mountTime;
      console.timeEnd("TranscriptionProvider mount");
      logger.info('📝 [TranscriptionContext] Provider lifecycle:', {
        mountDuration,
        updateCount: performanceRef.current.updateCount
      });
      
      // Ensure cleanup on unmount
      dispatch({ type: "CLEANUP" });
    };
  }, []);

  // Track state updates
  useEffect(() => {
    performanceRef.current.updateCount++;
    logger.debug('📝 [TranscriptionContext] State updated:', {
      length: state.transcript?.length ?? 0,
      isRecording: state.isRecording,
      isPaused: state.isPaused,
      currentIndex: state.currentIndex,
      updateCount: performanceRef.current.updateCount
    });
  }, [state]);

  // Keep ref in sync with current state
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Speech recognition setup and cleanup
  useEffect(() => {
    if (!state.isRecording) return;

    let recognition;
    try {
      // Check for browser support
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        throw new Error('Speech recognition not supported');
      }

      recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;

      const handleResult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        
        dispatch({ 
          type: "ADD_TRANSCRIPT_LINE", 
          payload: { text: transcript, timestamp: Date.now() }
        });
      };

      recognition.addEventListener('result', handleResult);
      recognition.start();

      logger.info('🎤 Speech recognition started');

      return () => {
        recognition.removeEventListener('result', handleResult);
        recognition.stop();
        logger.info('🎤 Speech recognition stopped');
      };
    } catch (error) {
      logger.error('🎤 Speech recognition error:', error);
      dispatch({ type: "STOP_RECORDING" });
    }
  }, [state.isRecording]);

  const startRecording = useCallback(() => {
    logger.info("⏺️ Starting recording");
    dispatch({ type: "START_RECORDING" });
  }, []);

  const stopRecording = useCallback(() => {
    logger.info("⏹️ Stopping recording");
    dispatch({ type: "STOP_RECORDING" });
  }, []);

  const addTranscriptLine = useCallback((line) => {
    dispatch({ type: "ADD_TRANSCRIPT_LINE", payload: line });
  }, []);

  const resetTranscript = useCallback(() => {
    dispatch({ type: "RESET_TRANSCRIPT" });
  }, []);

  const setTranscript = useCallback((transcript) => {
    dispatch({ type: "SET_TRANSCRIPT", payload: transcript });
  }, []);

  const setCurrentIndex = useCallback((index) => {
    dispatch({ type: "SET_CURRENT_INDEX", payload: index });
  }, []);
  
  const setIsRecording = useCallback((value) => {
    if (value) {
      startRecording();
    } else {
      stopRecording();
    }
  }, [startRecording, stopRecording]);

  const setIsPaused = useCallback((value) => {
    dispatch({ type: value ? "PAUSE_RECORDING" : "RESUME_RECORDING" });
  }, []);

  const onHighlightSummary = useCallback((summary) => {
    // Implement highlight logic if needed
    logger.debug("Highlighting summary:", summary);
  }, []);

  // Memoize the context value
  const value = useMemo(() => ({
    transcript: state.transcript,
    isRecording: state.isRecording,
    isPaused: state.isPaused,
    currentIndex: state.currentIndex,
    startRecording,
    stopRecording,
    addTranscriptLine,
    resetTranscript,
    setTranscript,
    setCurrentIndex,
    setIsRecording,
    setIsPaused,
    onHighlightSummary,
  }), [
    state.transcript,
    state.isRecording,
    state.isPaused,
    state.currentIndex,
    startRecording,
    stopRecording,
    addTranscriptLine,
    resetTranscript,
    setTranscript,
    setCurrentIndex,
    setIsRecording,
    setIsPaused,
    onHighlightSummary
  ]);

  return (
    <TranscriptionContext.Provider value={value}>
      {children}
    </TranscriptionContext.Provider>
  );
}

export function useTranscription() {
  const context = useContext(TranscriptionContext);
  if (!context) {
    throw new Error('useTranscription must be used within a TranscriptionProvider');
  }
  return context;
}
